import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Address } from '@kion/data-models';
import { debounce } from 'rxjs/operators';
import { interval } from 'rxjs';

@Component({
  selector: 'kion-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss']
})
export class AddressFormComponent implements OnInit, OnChanges{

  @Input() titleOptions: {key: string, value: string}[];
  @Input() address: Address;
  @Input() emailConfirmationVisible: boolean;
  @Input() mobileVisible: boolean;
  @Input() customerNumberVisible: boolean;
  @Input() isoCode: string;

  // tslint:disable-next-line:no-output-rename
  @Output('changed') addressChangedEmitter: EventEmitter<Partial<Address>> = new EventEmitter<Partial<Address>>();

  form: FormGroup;

  constructor(formBuilder: FormBuilder) {
    this.form = formBuilder.group(
      {
        salutation: new FormControl("", []),
        firstName: new FormControl(null, [Validators.required, Validators.minLength(1), Validators.maxLength(50)]),
        lastName: new FormControl(null, [Validators.required, Validators.minLength(1), Validators.maxLength(50)]),
        email: new FormControl(null, [Validators.required, Validators.email]),
        emailConfirmation: new FormControl(null, [Validators.required, this.checkEmail]),
        company: new FormControl(null, [Validators.minLength(1), Validators.maxLength(50)]),
        vatId: new FormControl(null),
        customerNumber: new FormControl(null),
        address: new FormControl(null, [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
        additionalAddress: new FormControl(null, [Validators.minLength(1), Validators.maxLength(100)]),
        zipCode: new FormControl(null, [Validators.required, Validators.minLength(4), Validators.maxLength(10)]),
        city: new FormControl(null, [Validators.required, Validators.minLength(1), Validators.maxLength(60)]),
        phone: new FormControl(null, [Validators.required, Validators.minLength(1), Validators.maxLength(25)]),
        mobile: new FormControl(null, [Validators.minLength(1), Validators.maxLength(25)])
      }
    )
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(debounce(() => interval(1000)))
      .subscribe(
        values =>  {
          if (!values.emailConfirmation && !this.emailConfirmationVisible){
            this.form.get('emailConfirmation').patchValue(values.email);
          }
          if (this.form.valid) {
            let address = {
              titleCode: values.salutation,
              firstName: values.firstName,
              lastName: values.lastName,
              email: values.email,
              companyName: values.company,
              vatId: values.vatId,
              customerId: values.customerNumber,
              line1: values.address,
              line2: values.additionalAddress,
              postalCode: values.zipCode,
              town: values.city,
              phone: values.phone,
              cellphone: values.mobile
            };
            if (this.address?.id) {
              address = {
                ...this.address,
                ...address
              }
            }
            if (this.form.touched || this.form.dirty) {
              this.addressChangedEmitter.emit(address);
            }
          }
        }
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.address) {
      if (changes.address.currentValue !== changes.address.previousValue) {
        console.log("changed");
        this.form.patchValue({
          salutation: this.address.titleCode,
          firstName: this.address.firstName,
          lastName: this.address.lastName,
          email: this.address.email,
          emailConfirmation: this.address.email,
          company: this.address.companyName,
          vatId: this.address.vatId,
          customerNumber: this.address.customerId,
          address: this.address.line1,
          additionalAddress: this.address.line2,
          zipCode: this.address.postalCode,
          city: this.address.town,
          phone: this.address.phone,
          mobile: this.address.cellphone
        });
        this.form.markAsUntouched();
        this.form.markAsPristine();
      }
    }
  };

  checkEmail(group: FormGroup) {
    const email = group.get('email')?.value;
    const emailConfirmation = group.get('emailConfirmation')?.value;

    return email === emailConfirmation ? null : { notSame: true }
  }

  get salutationField() {
    return this.form.get('salutation');
  }
  get firstNameField() {
    return this.form.get('firstName');
  }
  get lastNameField() {
    return this.form.get('lastName');
  }
  get emailField() {
    return this.form.get('email');
  }
  get emailConfirmationField() {
    return this.form.get('emailConfirmation');
  }
  get companyField() {
    return this.form.get('company');
  }
  get vatIdField() {
    return this.form.get('vatId');
  }
  get customerNumberField() {
    return this.form.get('customerNumber');
  }
  get addressField() {
    return this.form.get('address');
  }
  get additionalAddressField() {
    return this.form.get('additionalAddress');
  }
  get zipCodeField() {
    return this.form.get('zipCode');
  }
  get cityField() {
    return this.form.get('city');
  }
  get phoneField() {
    return this.form.get('phone');
  }
  get mobileField() {
    return this.form.get('mobile');
  }
}
